import { useEffect, useRef } from 'react';
import { useFormContext } from 'react-hook-form';
import RHFDictionary from '../RHFDictionary';
import { useProcurementDeliveries } from 'iomart-hooks';
import type { Columns } from 'src/@types/common';

// Список заявок одной строкой (например: "REQ-001, REQ-002")
const requestsDisplay = (row: any) => {
  const reqs = row?.requests;
  if (!Array.isArray(reqs) || reqs.length === 0) return '-';
  return reqs.map((r: any) => r?.documentNumber ?? `#${r?.id}`).filter(Boolean).join(', ');
};

const DICTIONARY_COLUMNS: (Columns & { minWidth?: number; maxWidth?: number })[] = [
  {
    name: 'documentNumber',
    subname: 'documentDate',
    label: 'number',
    format: undefined,
    subFormat: 'date',
    align: 'left',
    width: 120,
    minWidth: 100,
    maxWidth: 120,
  },
  {
    name: 'contract',
    label: 'Contract',
    format: 'selector',
    params: { selector: (c: any) => c?.name ?? '-' },
    nameLines: 2,
    minWidth: 120,
    width: 180,
    maxWidth: 220,
  },
  {
    name: 'requests',
    label: 'Requests',
    format: (item: any, _name: string) => requestsDisplay(item),
    nameLines: 2,
    minWidth: 140,
    width: 200,
    maxWidth: 260,
  },
  {
    name: 'comment',
    label: 'comment',
    align: 'left',
    nameLines: 2,
    minWidth: 120,
    width: 150,
    maxWidth: 180,
  },
];

type RHFProcurementDeliveriesProps = {
  name: string;
  label?: string;
  multiple?: boolean;
  disabled?: boolean;
  /** Имя поля формы с контрактом (объект { id, name }); при выборе контракта поставки фильтруются по нему */
  contractFieldName?: string;
  /** Ширина Drawer справочника */
  drawerWidth?: Record<string, string | number>;
};

export default function RHFProcurementDeliveries({
  name,
  label = 'Deliveries',
  multiple = false,
  disabled,
  contractFieldName,
  drawerWidth = { xs: '100%', sm: 640 },
}: RHFProcurementDeliveriesProps) {
  const { watch, setValue } = useFormContext();
  const contract = contractFieldName ? watch(contractFieldName) : undefined;
  const contractId = contract?.id ?? undefined;
  const prevContractIdRef = useRef<number | undefined | null>(null);
  const isFirstRun = useRef(true);

  useEffect(() => {
    if (!contractFieldName) return;
    if (isFirstRun.current) {
      isFirstRun.current = false;
      prevContractIdRef.current = contractId;
      return;
    }
    if (prevContractIdRef.current !== contractId) {
      prevContractIdRef.current = contractId;
      setValue(name, multiple ? [] : null, { shouldValidate: true, shouldDirty: true });
    }
  }, [contractId, contractFieldName, name, setValue, multiple]);

  const contractName = contract?.name ?? undefined;

  const filters =
    contractId != null
      ? {
          contract_id: contractId,
          ...(contractName != null && { contract_id_display: contractName }),
        }
      : {};

  const appliedReferenceFilters =
    contractId != null ? [{ key: 'contract_id', label: 'Contract' }] : [];

  return (
    <RHFDictionary
      name={name}
      label={label}
      useHook={useProcurementDeliveries}
      columns={DICTIONARY_COLUMNS}
      multiple={multiple}
      optionsLabel="documentNumber"
      getOptionLabel={(opt: any) =>
        opt?.documentNumber
          ? `${opt.documentNumber}${opt?.contract?.name ? ` (${opt.contract.name})` : ''}`
          : `#${opt?.id}` || ''
      }
      searchName="name"
      pageSize={10}
      disabled={disabled}
      filters={filters}
      appliedReferenceFilters={appliedReferenceFilters}
      drawerWidth={drawerWidth}
    />
  );
}
